import { useIsPinned } from '@hooks/useIsPinned'
import { useStyles } from '@hooks/useStyles'
import Logo from '@images/checkout/logo.svg'
import { isCheckoutRoute } from '@pages/Checkout/helpers/isCheckoutRoute'
import { LANDINGS } from '@routes/path'
import { Button, Close, CustomLink, Icon, Image, Spreader } from '@ui-kit'
import { FC, Fragment, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Row } from 'simple-flexbox'

import { useCheckoutContext } from '../contexts'
import styles from './CheckoutWrapper.module.scss'

interface CheckoutWrapperProps {
  children: ReactNode
}

export const CheckoutWrapper: FC<CheckoutWrapperProps> = ({ children }) => {
  const { isFirstOrLastStep, goToPreviousStep, goToStep, steps, isLastStep } =
    useCheckoutContext()

  const navigate = useNavigate()

  const location = useLocation()

  const isPinned = useIsPinned(`.pinnable-checkout-steps`, `-68px 0px 0px 0px`)

  const topbarBreadcrumbsStyles = useStyles({
    [styles['topbar-breadcrumbs__container']]: true,
    [styles['topbar-breadcrumbs__container--pinned']]: isPinned
  })

  const handleClose = () => navigate(LANDINGS.LANDINGS)

  const handleGoToBeforeStep = () => {
    const { pathname } = location

    if (isCheckoutRoute(pathname)) {
      goToPreviousStep(pathname)
    }
  }

  return (
    <div className={styles['checkout-wrapper']}>
      <Row className={styles['topbar-breadcrumbs__wrapper']}>
        <Row className={topbarBreadcrumbsStyles} justifyContent='space-between'>
          <Row alignItems='center'>
            {isFirstOrLastStep ? (
              <span
                className={styles['topbar-breadcrumbs__back-button--hidden']}
              />
            ) : (
              <Button
                variant='text-neutral'
                icon='icon-arrow-left'
                onClick={handleGoToBeforeStep}
                className={styles['topbar-breadcrumbs__back-button']}
                data-testid='checkout-back-button'
              />
            )}

            <Spreader spread={20} />

            <Image src={Logo} width='27px' height='20px' />

            <Spreader spread={50} />

            <Row alignItems='baseline'>
              {steps.map(({ path, label }, index) => {
                const currentIndex = steps.findIndex(
                  step => step.path === location.pathname
                )

                const isDisabled = index > currentIndex
                const isActive = path === location.pathname

                const canBeClicked = !isActive && !isLastStep

                return (
                  <Fragment key={path}>
                    <CustomLink
                      variant='neutral'
                      weight={600}
                      onClick={() => {
                        if (canBeClicked) {
                          goToStep(path)
                        }
                      }}
                      isDisabled={isDisabled}
                      size={16}
                      data-testid={`checkout-step-${label.toLowerCase()}`}
                      className={
                        !canBeClicked
                          ? styles['topbar-breadcrumbs__customlink']
                          : ''
                      }
                    >
                      {label}
                    </CustomLink>

                    {index < steps.length - 1 && (
                      <Fragment>
                        <Spreader spread={5} />

                        <Icon
                          icon='icon-chevron-right'
                          size={14}
                          color={
                            index >= currentIndex ? 'neutral-2' : 'neutral-6'
                          }
                        />

                        <Spreader spread={5} />
                      </Fragment>
                    )}
                  </Fragment>
                )
              })}
            </Row>
          </Row>

          <Close onClick={handleClose} data-testid='checkout-close-button' />
        </Row>
      </Row>

      <div className='pinnable-checkout-steps' />

      {children}
    </div>
  )
}

CheckoutWrapper.displayName = 'CheckoutWrapper'
